import {
  BasketLink,
  FeedbackForm,
  FeedbackFormVariant,
  HoldingPage,
  ImpactShopHero,
  ImpactShopHomeContent,
  TopNavigation,
} from "@ecologi/react-components";
import {
  ImpactShopProductType,
  locationCurrencyCodeMapper,
  PlatformFeature,
} from "@ecologi/shared";
import {
  GetServerSidePropsContext,
  NextApiRequest,
  NextApiResponse,
} from "next";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { Head } from "../../components";
import { auth0 } from "../../lib";
import { createGeneralFeedback } from "../../lib/api/general-feedback";
import { getAllImpactShopProducts } from "../../lib/api/impact-shop";
import { withFeatureFlagGuard } from "../../lib/auth/with-feature-flag-guard";
import { withLogging } from "../../lib/ssr/logGetServerSideProps";
import { useStore } from "../../store";
import { useFetchImpactShopStore } from "../../store/impact-shop";

export const IMPACT_SHOP_UNAUTHENTICATED_REDIRECT = "/business/impact-shop";

const useImpactShopStore = useFetchImpactShopStore();

const ImpactShopPageComponent = ({
  products,
}: {
  products: ImpactShopProductType[];
}) => {
  const { location, user } = useStore();
  const { asPath } = useRouter();
  const { totalNumberOfProductsInBasket, setCurrency, currency } =
    useImpactShopStore();

  useEffect(() => {
    const currency =
      user?.preferredCurrency ||
      locationCurrencyCodeMapper[location.countryCode];

    setCurrency(currency);
  }, [user?.preferredCurrency, location]);

  return (
    <>
      <Head title="Impact shop" />
      <TopNavigation
        loggedOutNav={!user}
        rightSlot={
          <BasketLink
            href="/impact-shop/basket"
            itemQuantity={totalNumberOfProductsInBasket()}
          />
        }
      />
      <ImpactShopHero />
      <ImpactShopHomeContent products={products} currency={currency} />
      {user && (
        <FeedbackForm
          onSubmit={createGeneralFeedback}
          currentUser={user}
          currentPath={asPath}
          variant={FeedbackFormVariant.SNOWDROP}
        />
      )}
    </>
  );
};

export const ImpactShopPage = withFeatureFlagGuard(
  ImpactShopPageComponent,
  () => <HoldingPage />,
  { [PlatformFeature.Maintenance]: false }
);

export const getServerSideProps = withLogging(
  async (context: GetServerSidePropsContext) => {
    const tokenCache = auth0.tokenCache(
      context.req as NextApiRequest,
      context.res as NextApiResponse
    );

    const session = await auth0.getSession(context.req);

    const products = await getAllImpactShopProducts(
      session ? tokenCache : undefined
    );

    return {
      props: {
        layoutProps: {
          hideHeader: true,
          isWhiteBody: true,
          isProfile: true,
        },
        products,
      },
    };
  }
);

export default ImpactShopPage;
